// YERİ: src/app/domains/site/sections/hospitality.sections.ts
//
// AĞIRLAMA BÖLÜMLERİ — canlı menü, oda tipleri, otel olanakları.
//
// ── `menu-display`ten FARKI ───────────────────────────────────────────────
// Web-builder'da zaten bir `menu-display` bölümü var ama içeriği ELLE
// yazılıyor: restoran fiyat değiştirdiğinde POS'taki menüyü güncelliyor,
// sitedeki menü eski fiyatla kalıyor ve müşteri hesabı görünce şaşırıyor.
// `live-menu` aynı görünümü RstMenuCategory/RstMenuItem'dan besliyor —
// tek yer güncelleniyor.
//
// Tükenen kalem (IsAvailable = false) sunucuda eleniyor: bugün yapılmayan
// yemek menüde durmamalı.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import { paraBirimi } from './community.sections';
import {
  TenantWebService, TenantMenuCategory, TenantRoomType, TenantAmenity,
} from '../core/tenant-web.service';

// ══════════════════════════════════════════════════════════════════════════
// CANLI MENÜ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-live-menu',
  standalone: true,
  template: `
    @if (kategori().length) {
    <section id="menu" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 space-y-10">
          @for (k of kategori(); track k.id) {
            <div>
              <h3 class="border-b-2 pb-2 text-lg font-bold uppercase tracking-wide text-neutral-900 dark:text-white"
                  style="border-color:var(--site-accent)">{{ k.name }}</h3>
              <ul class="mt-4 space-y-4">
                @for (i of k.items; track i.id) {
                  <li class="flex gap-4">
                    @if (i.photoUrl) {
                      <img [src]="i.photoUrl" [alt]="i.name" class="h-16 w-16 shrink-0 rounded-lg object-cover" />
                    }
                    <div class="min-w-0 flex-1">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="font-semibold text-neutral-900 dark:text-white">{{ i.name }}</span>
                        <!-- Noktalı çizgi: klasik menü dizgisi. Fiyat sağda
                             sabit kalsın diye ayrı sütun. -->
                        <span class="flex-1 border-b border-dotted border-neutral-300 dark:border-neutral-700"></span>
                        @if (i.price > 0) {
                          <span class="shrink-0 font-bold tabular-nums" style="color:var(--site-primary)">{{ fiyat(i.price, i.currency) }}</span>
                        } @else {
                          <!-- Fiyatı girilmemiş kalem "0,00" basılsaydı ÜCRETSİZ
                               sanılırdı; kasada tartışma çıkar. -->
                          <span class="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</span>
                        }
                      </div>
                      @if (i.description) {
                        <p class="mt-0.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ i.description }}</p>
                      }
                      <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                        @if (i.prepMinutes) { <span>{{ i.prepMinutes }} {{ text.t('mn.minutes') }}</span> }
                        <!-- Alerjen gizlenmez: sağlıkla ilgili bir bilgiyi
                             "sayfa kalabalık olmasın" diye saklamak olmaz. -->
                        @if (i.allergens) {
                          <span class="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{{ text.t('mn.allergens') }}: {{ i.allergens }}</span>
                        }
                      </div>
                    </div>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class LiveMenuSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected kategori = signal<TenantMenuCategory[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getRestaurantMenu(id).subscribe({
      next: (l) => this.kategori.set(l),
      error: () => this.kategori.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.menu')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.menumuz')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected fiyat = (p: number, c: string | null | undefined) => paraBirimi(p, c, this.text.lang(), this.text.currencyKey());
}

// ══════════════════════════════════════════════════════════════════════════
// ODA TİPLERİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-roomtype-grid',
  standalone: true,
  template: `
    @if (oda().length) {
    <section id="rooms" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (o of oda(); track o.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
              <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ o.name }}</h3>
              @if (o.basePrice > 0) {
                <p class="mt-3 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">
                  {{ fiyat(o.basePrice) }}
                  <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">{{ text.t('rm.perNight') }}</span>
                </p>
              } @else {
                <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</p>
              }
              <ul class="mt-4 space-y-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                <li>· {{ text.t('rm.upTo') }} {{ o.maxOccupancy }} {{ text.t('rm.guests') }}</li>
                @if (o.bedConfig) { <li>· {{ o.bedConfig }}</li> }
                @if (o.sizeM2) { <li>· {{ o.sizeM2 }} m²</li> }
              </ul>
              @if (olanaklar(o).length) {
                <div class="mt-4 flex flex-wrap gap-1.5">
                  @for (a of olanaklar(o); track a) {
                    <span class="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ a }}</span>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class RoomTypeGridSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected oda = signal<TenantRoomType[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getHotelRoomTypes(id).subscribe({
      next: (l) => this.oda.set(l),
      error: () => this.oda.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.odalar')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.odaTiplerimiz')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  /**
   * Oda tipi tablosunda para birimi kolonu YOK. Bölüm ayarında elle bir kod
   * verilmemişse SİTENİN birimine düşer (şirketin Currency alanı) — burada
   * sabit 'TRY' duruyordu ve Toronto'daki otel gecelik ücretini ₺ ilan ediyordu.
   */
  protected fiyat = (p: number) =>
    paraBirimi(p, String(cfg(this.config(), 'currency', '')) || null, this.text.lang(), this.text.currencyKey());

  /** Serbest metin olanak listesi: virgül ya da satır sonu ile ayrılmış olabilir. */
  protected olanaklar = (o: TenantRoomType): string[] =>
    (o.amenitiesText || '').split(/,|\r?\n/).map(t => t.trim()).filter(Boolean).slice(0, 6);
}

// ══════════════════════════════════════════════════════════════════════════
// OTEL OLANAKLARI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-hotel-amenities',
  standalone: true,
  template: `
    @if (olanak().length) {
    <section id="amenities" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
        </div>

        <div class="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          @for (a of olanak(); track a.id) {
            <div class="rounded-xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
              <div class="flex items-baseline justify-between gap-2">
                <h3 class="font-semibold text-neutral-900 dark:text-white">{{ a.name }}</h3>
                @if (a.kind) {
                  <span class="shrink-0 text-xs uppercase tracking-wide" style="color:var(--site-accent)">{{ a.kind }}</span>
                }
              </div>
              @if (a.description) {
                <p class="mt-1.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ a.description }}</p>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class HotelAmenitiesSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected olanak = signal<TenantAmenity[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getHotelAmenities(id).subscribe({
      next: (l) => this.olanak.set(l),
      error: () => this.olanak.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.olanaklar')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.otelimizdeNelerVar')));
}
